import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { normalizeEbayFunctionError } from '@/lib/ebayFunctionErrors';
import { activity } from '@/store/activityStore';

const ACTIVITY_ID = 'bulk-ebay-list';

export type BulkEbayRowStatus =
  | 'queued'
  | 'listing'
  | 'listed'
  | 'failed'
  | 'skipped';

export interface BulkEbayRowState {
  status: BulkEbayRowStatus;
  error?: string;
  view_url?: string | null;
}

interface RunOpts {
  itemIds: string[];
  concurrency?: number;
  /** Lets the global activity bar reopen the dialog that launched the run. */
  onOpen?: () => void;
}

/**
 * Client-side fan-out over the existing `ebay-list-item` edge function.
 * Keeps per-item granularity so the dialog can surface row-by-row state
 * (queued → listing → listed | failed) and reuse all server-side validation.
 */
export function useBulkEbayList() {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Record<string, BulkEbayRowState>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const cancelledRef = useRef(false);

  const reset = useCallback(() => {
    setRows({});
    setIsRunning(false);
    setDone(0);
    setTotal(0);
    cancelledRef.current = false;
    activity.end(ACTIVITY_ID);
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    activity.end(ACTIVITY_ID);
  }, []);

  const setRow = useCallback((id: string, patch: Partial<BulkEbayRowState>) => {
    setRows((prev) => ({ ...prev, [id]: { ...(prev[id] ?? { status: 'queued' }), ...patch } }));
  }, []);

  const listOne = useCallback(
    async (itemId: string, format: 'fixed_price' | 'auction' = 'fixed_price'): Promise<boolean> => {
      setRow(itemId, { status: 'listing' });
      try {
        const fn = format === 'auction' ? 'ebay-list-auction' : 'ebay-list-item';
        const { data, error } = await supabase.functions.invoke(fn, {
          body: { item_id: itemId },
        });
        if (error) {
          const response = (error as any)?.context;
          if (response instanceof Response) {
            const body = await response.json().catch(() => null);
            const msg = normalizeEbayFunctionError(body, error.message).message;
            setRow(itemId, { status: 'failed', error: msg });
            return false;
          }
          setRow(itemId, { status: 'failed', error: error.message });
          return false;
        }
        if ((data as any)?.error) {
          setRow(itemId, { status: 'failed', error: normalizeEbayFunctionError(data, 'eBay listing failed').message });
          return false;
        }
        setRow(itemId, {
          status: 'listed',
          view_url: (data as any)?.view_url ?? null,
        });
        return true;
      } catch (e: any) {
        setRow(itemId, { status: 'failed', error: e?.message ?? String(e) });
        return false;
      }
    },
    [setRow]
  );

  const run = useCallback(
    async ({ itemIds, concurrency = 3, onOpen }: RunOpts) => {
      if (itemIds.length === 0) return;
      cancelledRef.current = false;
      setIsRunning(true);
      setTotal(itemIds.length);
      setDone(0);
      const initial: Record<string, BulkEbayRowState> = {};
      for (const id of itemIds) initial[id] = { status: 'queued' };
      setRows(initial);

      // Tier 3: keep a thin global bar alive even if the dialog is closed.
      activity.start({
        id: ACTIVITY_ID,
        label: 'Listing on eBay',
        done: 0,
        total: itemIds.length,
        failed: 0,
        onOpen,
      });

      // Prefetch per-item listing format so we route auction items to the trading-API function
      const formatById = new Map<string, 'fixed_price' | 'auction'>();
      try {
        const { data } = await (supabase as any)
          .from('user_items')
          .select('id,ebay_listing_format')
          .in('id', itemIds);
        (data ?? []).forEach((r: any) => {
          formatById.set(r.id, r.ebay_listing_format === 'auction' ? 'auction' : 'fixed_price');
        });
      } catch {
        // Fall back to fixed_price if the prefetch fails
      }

      const queue = [...itemIds];
      const workers: Promise<void>[] = [];
      let completed = 0;
      let failedCount = 0;

      const worker = async () => {
        while (queue.length > 0) {
          if (cancelledRef.current) return;
          const next = queue.shift();
          if (!next) return;
          const ok = await listOne(next, formatById.get(next) ?? 'fixed_price');
          completed += 1;
          if (!ok) failedCount += 1;
          setDone((d) => d + 1);
          activity.update(ACTIVITY_ID, { done: completed, failed: failedCount });
        }
      };

      for (let i = 0; i < Math.min(concurrency, itemIds.length); i++) {
        workers.push(worker());
      }
      await Promise.all(workers);

      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['ebay-listing'] });
      queryClient.invalidateQueries({ queryKey: ['my-active-ebay-item-ids'] });
      setIsRunning(false);
      activity.end(ACTIVITY_ID);
    },
    [listOne, queryClient]
  );

  const retry = useCallback(
    async (itemId: string) => {
      // Re-check format at retry time in case the user changed it via Edit
      let format: 'fixed_price' | 'auction' = 'fixed_price';
      try {
        const { data } = await (supabase as any)
          .from('user_items')
          .select('ebay_listing_format')
          .eq('id', itemId)
          .maybeSingle();
        if (data?.ebay_listing_format === 'auction') format = 'auction';
      } catch {}
      await listOne(itemId, format);
    },
    [listOne]
  );


  return { rows, isRunning, done, total, run, retry, reset, cancel };
}
